export const removeWhitespaces = (input: string) => input.replace(/\s/g, "");

/**
 * Type Narrow empty objects
 * https://stackoverflow.com/a/52202691
 */
type Empty =
  | never[] // Empty Array
  | Record<string, never> // Empty Object
  | ""; // Empty String

type Bottom<T> = T extends string
  ? ""
  : T extends any[]
  ? never[]
  : T extends Record<string, unknown>
  ? Record<string, never>
  : never;

export function isEmpty<T extends string | any[] | Record<string, unknown>>(
  subject: T | Empty
): subject is Bottom<T> {
  switch (typeof subject) {
    case "object":
      return Object.keys(subject).length === 0;
    case "string":
      return subject === "";
    default:
      return false;
  }
}

/**
 * Test if a url is internal or to an external link
 * @example
 * const isExternal = isExternalLink(urlString);
 */
export const isExternalLink = (url: string) => {
  const externalUrlPattern = /^(https?:)?\/\//i;
  return externalUrlPattern.test(url);
};

type RemoveLeadingSlash<T extends string | undefined> = T extends `/${infer U}`
  ? U
  : T;

/**
 * Remove leading slash from a slug or url
 */
export const removeLeadingSlash = <T extends string | undefined>(
  url: T
): T extends undefined ? undefined : RemoveLeadingSlash<T> => {
  if (url === undefined) return url as T extends undefined ? T : never;
  if (url.startsWith("/")) {
    return url.substring(1) as T extends undefined
      ? never
      : RemoveLeadingSlash<T>;
  }
  return url as T extends undefined ? undefined : RemoveLeadingSlash<T>;
};

type RemoveTrailingSlash<T extends string | undefined> = T extends `${infer U}/`
  ? U
  : T;

/**
 * Remove trailing slash from a slug or url
 */
export const removeTrailingSlash = <T extends string | undefined>(
  url: T
): T extends undefined ? undefined : RemoveTrailingSlash<T> => {
  if (url === undefined) return url as T extends undefined ? T : never;
  return url.replace(/\/$/, "") as T extends undefined
    ? never
    : RemoveTrailingSlash<T>;
};

/**
 * Capitalizes the text content inside HTML tags.
 *
 * This function utilizes a regex pattern to match any HTML tag and its content.
 * It then capitalizes the text content while preserving the original casing of the tag itself.
 *
 * @param str - The input HTML string to be processed.
 * @returns The capitalized HTML string.
 */
const capitalizeInsideHTMLTags = (str: string): string => {
  const capitalize = (text: string) => {
    return text
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const regex = /<([\w-]+)>([^<]+)<\/\1>/g;

  return str.replace(regex, (match, tagName, content) => {
    return `<${tagName}>${capitalize(content)}</${tagName}>`;
  });
};

export const containsHTMLTags = (str: string): boolean => {
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(str);
};

type CapitalizeFirstLetterOptions = {
  minWordLength?: number;
};

export const capitalizeFirstLetter = (
  str?: string,
  options?: CapitalizeFirstLetterOptions
): string | undefined => {
  if (!str) return str;

  const minWordLength = options?.minWordLength ?? 3;

  if (containsHTMLTags(str)) {
    return capitalizeInsideHTMLTags(str);
  } else {
    return str
      .split(" ")
      .map(word =>
        word.length > minWordLength - 1
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word
      )
      .join(" ");
  }
};

export const removeTailingDot = (s: string) => s.replace(/\.$/, "");
